import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import useChatStore from 'stores/chatStore';
import { MessageType } from 'types/chat.types';
import MessageBubble from 'components/organisms/chat/MessageBubble';

const SCROLL_DEBOUNCE_MS = 120;

const MessageList: React.FC = () => {
  const { messages } = useChatStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const scrollKey = useMemo(() => {
    const last = messages[messages.length - 1];
    const stepCount =
      last?.type === MessageType.REASONING ? (last as any).steps?.length ?? 0 : 0;
    return `${messages.length}-${stepCount}`;
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    const el = messagesEndRef.current;
    if (!el) return;

    const container = el.closest('.overflow-y-auto');
    if (!container) {
      el.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Only auto-scroll if user is already near the bottom (within 300px).
    // This prevents hijacking the scroll when a user has scrolled up to
    // review earlier content.
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom > 300) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(scrollToBottom, SCROLL_DEBOUNCE_MS);
    return () => clearTimeout(debounceTimer.current);
  }, [scrollKey, scrollToBottom]);

  if (messages.length === 0) {
    return (
      <div className="w-full max-w-3xl flex flex-col items-center justify-center h-full text-center py-12 px-4">
        <img
          src="/classfi-logo.png"
          alt="Classfi"
          className="w-16 h-16 mb-6"
        />
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          What will you Classfi today?
        </h1>
        <p className="text-base text-gray-600 max-w-2xl">
          Enter your product's description, country of origin, and its composition or ingredients
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl flex flex-col gap-4 pb-24">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
